import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useSelector } from "react-redux";
import { ReactComponent as DashboardIcon } from "icons/dashboard-icon.svg";
import { ReactComponent as SearchIcon } from "icons/search-icon.svg";
import { ReactComponent as SupplierIcon } from "icons/supplier-icon.svg";
import { ReactComponent as CompaniesIcon } from "icons/users.svg";
import { FiUsers } from "react-icons/fi";
import { getProjects } from "actions/RFQ/projectsAction";
import { getRfqPerPage } from "actions/RFQ/rfqsAction";
import { hasCompaniesListingAccess } from "utils/helpers";
import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import { useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { useCurrentUserHasDashboard, useHasPerformanceManagementAccess } from "hooks/featureFlags";
import { useUpdateUserTourFlags } from "newui/features/Auth/hooks/useUpdateUserTourFlags";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { ShareProfileModal } from "components/ShareProfile/ShareProfileModal";
import { ExecutiveSwitchButton } from "newui/features/ExecutiveView/components/ExecutiveSwitchButton";
import { AppMenuHeader } from "./AppMenuHeader";
import { AppMenuItem } from "./AppMenuItem/AppMenuItem";
import { AppMenuFooter } from "./AppMenuFooter";
import { AppMenuCommonBottomItems } from "./AppMenuCommonBottomItems";
import { AppMenuItemRfx } from "./AppMenuItem/AppMenuItemRFx";
import { RfxProjectsMenu } from "./RfxProjectsMenu";

interface AppMenuClientProps {
  isCollapsed: boolean;
  getProjects: (params: object, options?: object) => void;
  getRfqPerPage: (params: object, options?: object) => void;
  onNavigate?: () => void;
}

const AppMenuClientComponent: React.FC<AppMenuClientProps> = ({
  isCollapsed,
  getProjects,
  getRfqPerPage,
  onNavigate,
}) => {
  const currentCompany = useCurrentCompany();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const rfqNumber = useSelector(
    (state: any) => state.rfq?.rfqs?.rfqs?.active?.meta?.total
  );
  const { isRfqFeatureEnabled, setForceRestartTour } = useProfileContext();
  const [totalRFQs, setTotalRFQs] = useState<number>(rfqNumber || 0);
  const [displaySideMenu, setDisplaySideMenu] = useState(false);
  const [showSupplierMenu, setShowSupplierMenu] = useState<"init" | "yes" | "no">("init");
  const { logoutUser } = useAuthContext();
  const { isSuperAdmin } = useCurrentUser();
  const hasPerformanceManagement = useHasPerformanceManagementAccess();
  const updateUserTourFlags = useUpdateUserTourFlags();
  const hasDashboardAccess = useCurrentUserHasDashboard();
  const { clientPaths } = useNavigationPaths();

  useEffect(() => {
    if (rfqNumber !== undefined) {
      setTotalRFQs(rfqNumber);
    }
  }, [rfqNumber]);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchData = async () => {
      try {
        getProjects({ sort: "last_activity" }, { signal: abortController.signal });
        getRfqPerPage({}, { signal: abortController.signal });
      } catch (_e) {
        // ignore
      }
    };
    fetchData();
    return () => {
      abortController.abort();
    };
  }, [getProjects, getRfqPerPage]);

  useEffect(() => {
    if (!currentCompany) {
      return;
    }
    if (
      isSuperAdmin ||
      currentCompany.grouping === "procurer" ||
      currentCompany.grouping === "bidder_procurer"
    ) {
      setShowSupplierMenu("yes");
    } else {
      setShowSupplierMenu("no");
    }
  }, [currentCompany, isSuperAdmin]);

  useEffect(() => {
    if (showSupplierMenu !== "init") {
      setDisplaySideMenu(true);
    }
  }, [showSupplierMenu]);

  const handleResetTourFlags = async () => {
    await updateUserTourFlags(false, false);
    setForceRestartTour(true);
  };

  if (!clientPaths) {
    return null;
  }

  return (
    <div className="flex h-screen flex-col overflow-y-auto">
      <AppMenuHeader isCollapsed={isCollapsed} />
      <ExecutiveSwitchButton isCollapsed={isCollapsed} />

      {/* Top nav */}
      <div>
        {hasDashboardAccess && displaySideMenu && isRfqFeatureEnabled && (
          <AppMenuItem
            name="Dashboard"
            icon={<DashboardIcon />}
            link={clientPaths.dashboard}
            isCollapsed={isCollapsed}
            testId="procurer-dashboard"
            tourName="ProcurerDashboard"
            onNavigate={onNavigate}
          />
        )}
        {displaySideMenu && showSupplierMenu === "yes" && (
          <AppMenuItem
            name="Search Suppliers"
            icon={<SearchIcon />}
            link={clientPaths.supplierSearch}
            isCollapsed={isCollapsed}
            testId="search"
            tourName="SearchSuppliers"
            onNavigate={onNavigate}
          />
        )}
        {displaySideMenu && isRfqFeatureEnabled && (
          <RfxProjectsMenu
            isCollapsed={isCollapsed}
            onNavigate={onNavigate}
          />
        )}
        {displaySideMenu && isRfqFeatureEnabled && (
          <AppMenuItemRfx isCollapsed={isCollapsed} link={clientPaths.rfx.all} total={totalRFQs} onNavigate={onNavigate} />
        )}
        {displaySideMenu && hasPerformanceManagement && (
          <AppMenuItem
            name="Supplier Hub"
            icon={<FiUsers size={24} />}
            link={clientPaths.mySuppliers.list}
            isCollapsed={isCollapsed}
            testId="my-suppliers"
            onNavigate={onNavigate}
          />
        )}
        {displaySideMenu && (isSuperAdmin || hasCompaniesListingAccess(currentCompany)) && (
          <AppMenuItem
            name="Companies"
            icon={<CompaniesIcon />}
            link={clientPaths.companies}
            isCollapsed={isCollapsed}
            testId="companies"
            tourName="Companies"
            onNavigate={onNavigate}
          />
        )}
      </div>

      {/* Bottom nav — pinned to bottom, ordered top→bottom: Supplier List → … → Settings → Footer */}
      {displaySideMenu && (
        <div className="GuidedTourStart mt-auto shrink-0">
          {isRfqFeatureEnabled && (
            <AppMenuItem
              name="Supplier List"
              icon={<SupplierIcon />}
              link={clientPaths.supplierList.my}
              isCollapsed={isCollapsed}
              testId="supplier-list"
              tourName="SupplierList"
              onNavigate={onNavigate}
            />
          )}
          <AppMenuCommonBottomItems
            paths={clientPaths}
            isCollapsed={isCollapsed}
            onNavigate={onNavigate}
          />
          {isSuperAdmin && (
            <AppMenuItem
              name="Companies Admin"
              icon={<CompaniesIcon />}
              link={`/account/${currentCompany?.id}/accounts`}
              isCollapsed={isCollapsed}
              testId="companies-admin"
              tourName="CompaniesAdmin"
              onNavigate={onNavigate}
            />
          )}
          <AppMenuFooter
            isCollapsed={isCollapsed}
            onLogout={logoutUser}
            onHelpClick={handleResetTourFlags}
            showHelp
          />
        </div>
      )}

      <ShareProfileModal
        isOpen={isOpenModal}
        setIsOpen={setIsOpenModal}
      />
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.auth.user,
});

export const AppMenuClient = connect(mapStateToProps, {
  getProjects,
  getRfqPerPage,
})(AppMenuClientComponent);
